import type { CacheAssets } from "osrs-sdk/asset-manifest";

/** Colosseum requirements shared by gameplay and the asset builder. */
export const COLOSSEUM_ASSETS = {
  npcs: {
    fremennikWarbandArcher: {
      id: 12814,
      animations: { attack: 10850, defend: 10851, death: 10852 },
    },
    fremennikWarbandSeer: {
      id: 12815,
      animations: { attack: 10853, defend: 10854, death: 10855 },
    },
    fremennikWarbandBerserker: {
      id: 12816,
      animations: { attack: 10856, defend: 10857, death: 10858 },
    },
    jaguarWarrior: {
      id: 12810,
      animations: {
        // Jaguar warriors use the human Dragon Claws attack and player death.
        attack: 7514,
        death: 836,
      },
    },
    manticore: {
      id: 12818,
      animations: {
        tripleCharge: 10868,
        tripleThrow: 10869,
        death: 10866,
      },
    },
    javelinColossus: {
      id: 12817,
      animations: {
        rangeAttack: 10892,
        artilleryAttack: 10893,
        death: 10894,
      },
    },
    serpentShaman: {
      id: 12811,
      animations: {
        attack: 10859,
        death: 10860,
      },
    },
    minotaur: {
      id: 12812,
      animations: {
        defend: 10841,
        attack: 10843,
        heal: 10844,
        spawn: 10845,
        death: 10846,
      },
    },
    shockwaveColossus: {
      id: 12819,
      animations: {
        attack: 10903,
        death: 10895,
      },
    },
    solHeredit: {
      id: 12821,
      animations: {
        attack: 10883,
        attackAlt1: 10884,
        attackAlt2: 10885,
        attackAlt3: 10886,
        attackAlt4: 10887,
        attackAlt5: 10888,
        death: 10877,
      },
    },
  },
  regions: {
    colosseum: { id: 7216 },
  },
  models: {
    solWallA: { id: 50963, animations: { idle: 7508 } },
    solWallB: { id: 50964, animations: { idle: 7508 } },
  },
  spotAnims: {
    fremennikArcherProjectile: { id: 9 },
    fremennikSeerProjectile: { id: 130 },
    shockwaveColossusProjectile: { id: 1341 },
    serpentShamanProjectile: { id: 1459 },
    javelinColossusProjectile: { id: 2673 },
    javelinColossusArtillerySpear: { id: 2674 },
    javelinColossusArtilleryExplosion: { id: 2265 },
    solTripleAttackLong: { id: 2667 },
    solTripleAttackShort: { id: 2668 },
    solDust: { id: 2669 },
    mageOrb: { id: 2681 },
    rangeOrb: { id: 2683 },
    meleeOrb: { id: 2685 },
    sandPool: { id: 2698 },
    // Sol's crystal laser has direction-specific charge and firing graphics.
    // The orientation is baked into the spotanim animation rather than being
    // supplied as a runtime rotation.
    laserSouthCharge: { id: 2689 },
    laserWestCharge: { id: 2690 },
    laserNorthCharge: { id: 2691 },
    laserEastCharge: { id: 2692 },
    laserSouthFire: { id: 2693 },
    laserWestFire: { id: 2694 },
    laserNorthFire: { id: 2695 },
    laserEastFire: { id: 2696 },
    laserImpact: { id: 2697 },
  },
  sounds: {
    manticoreTripleThrowProjectile01: { id: 8382 },
    javelinColossusAttack: { id: 8348 }, // actually this seems to be the landing sound
    manticoreProjectile01: { id: 8406 },
    manticoreProjectile02: { id: 8400 },
    manticoreProjectile03: { id: 8383 },
    waveStartPrompt: { id: 2266 },
    waveStartStartHover: { id: 7986 },
    waveStartAcknowledged: { id: 8010 },
    solSpearStart: { id: 8147 },
    solShieldStart: { id: 8150 },
    solShieldEnd: { id: 8145 },
    solTripleStart: { id: 8211 },
    solTripleCharge1: { id: 8317 },
    solTripleCharge2: { id: 8274 },
    solTripleCharge3Short: { id: 8218 },
    solTripleCharge3Long: { id: 8113 },
    solTripleParry1: { id: 8140 },
    solTripleParry2: { id: 8171 },
    solTripleParry3: { id: 8242 },
    solGrappleCharge: { id: 8329 },
    solGrappleParry: { id: 8081 },
    solPoolSpawn: { id: 8053 },
    solPoolShriek: { id: 8093 },
    solLaserCharge: { id: 8253 },
    solLaserFire: { id: 8230 },
    varlColossiBossMeleeAttackTelegraphStab08: { id: 8047 },
    varlColossiBossDeathMetallicImpact10: { id: 8048 },
    varlColossiBossMeleeAttackTelegraphFootstep09: { id: 8051 },
    varlColossiBossShieldslamArmPrep03: { id: 8054 },
    varlColossiBossMeleeAttackTelegraphDislodge02: { id: 8055 },
    varlColossiBossMeleeAttackTelegraphTridentEndMove05: { id: 8056 },
    varlColossiBossMeleeAttackTelegraphStabImpact05: { id: 8057 },
    varlColossiBossMeleeAttackTelegraphStabImpact02: { id: 8058 },
    varlColossiBossShieldDropImpact02: { id: 8063 },
    varlColossiBossDeathFirstShieldDrop02: { id: 8068 },
    varlColossiBossDeathSecondFall02: { id: 8069 },
    varlColossiBossMeleeAttackTelegraphCharge13: { id: 8073 },
    varlColossiBossGrappleAttackFirstShieldDrop01: { id: 8075 },
    varlColossiBossMeleeAttackTelegraphCharge05: { id: 8078 },
    varlColossiBossMeleeAttackTelegraphFootstep08: { id: 8079 },
    varlColossiBossHiss02: { id: 8085 },
    varlColossiBossMeleeAttackTelegraphCharge03: { id: 8086 },
    varlColossiBossMeleeAttackTelegraphStab01: { id: 8087 },
    varlColossiBossMeleeAttackTelegraphLunge03: { id: 8088 },
    varlColossiBossDeathSecondFall01: { id: 8090 },
    varlColossiBossMeleeAttackTelegraphStab02: { id: 8092 },
    varlColossiBossMeleeAttackTelegraphDislodge01: { id: 8096 },
    varlColossiBossMeleeAttackTelegraphBass02: { id: 8097 },
    varlColossiBossDeathMetallicImpact03: { id: 8098 },
    varlColossiBossMeleeAttackTelegraphCharge01: { id: 8099 },
    varlColossiBossMeleeAttackTelegraphCharge06: { id: 8102 },
    varlColossiBossMeleeAttackTelegraphBass01: { id: 8103 },
    varlColossiBossDeathFirstFall03: { id: 8105 },
    varlColossiBossFootsteps03: { id: 8107 },
    varlColossiBossMeleeAttackTelegraphTridentEndMove03: { id: 8109 },
    varlColossiBossDeathRinging02: { id: 8115 },
    varlColossiBossShortHiss02: { id: 8117 },
    varlColossiBossFootsteps02: { id: 8118 },
    varlColossiBossDeathMetallicImpact09: { id: 8119 },
    varlColossiBossMeleeAttackTelegraphCharge02: { id: 8120 },
    varlColossiBossMeleeAttackTelegraphStabImpact01: { id: 8121 },
    varlColossiBossDeathMetallicImpact02: { id: 8122 },
    varlColossiBossMeleeAttackTelegraphStab03: { id: 8123 },
    varlColossiBossDeathSecondFall03: { id: 8126 },
    varlColossiBossMeleeAttackTelegraphLunge01: { id: 8127 },
    varlColossiBossDeathMetallicImpact08: { id: 8130 },
    varlColossiBossMeleeAttackTelegraphDislodge06: { id: 8131 },
    varlColossiBossDeathMetallicImpact04: { id: 8132 },
    varlColossiBossDeathWhoosh01: { id: 8133 },
    varlColossiBossMeleeAttackTelegraphFootstep03: { id: 8135 },
    varlColossiBossMeleeAttackTelegraphBackFootstep01: { id: 8137 },
    varlColossiBossDeathMetallicImpact06: { id: 8138 },
    varlColossiBossMeleeAttackTelegraphBassStep01: { id: 8139 },
    varlColossiBossFootsteps06: { id: 8143 },
    varlColossiBossShieldslamCharge05: { id: 8148 },
    varlColossiBossMeleeAttackTelegraphCharge08: { id: 8153 },
    varlColossiBossShieldslamCharge01: { id: 8154 },
    varlColossiBossMeleeAttackTelegraphStabImpact03: { id: 8155 },
    varlColossiBossDeathWhoosh03: { id: 8157 },
    varlColossiBossMeleeAttackTelegraphFootstep02: { id: 8160 },
    varlColossiBossMeleeAttackTelegraphStabImpact04: { id: 8162 },
    varlColossiBossMeleeAttackTelegraphCharge15: { id: 8163 },
    varlColossiBossMeleeAttackTelegraphBackFootstep02: { id: 8165 },
    varlColossiBossMeleeAttackTelegraphTridentEndMove02: { id: 8168 },
    varlColossiBossMeleeAttackTelegraphFootstep06: { id: 8169 },
    varlColossiBossMeleeAttackTelegraphTridentEndMove14: { id: 8170 },
    varlColossiBossShieldDropImpact01: { id: 8174 },
    varlColossiBossDeathMetallicImpact05: { id: 8175 },
    varlColossiBossDeathMetallicImpact07: { id: 8177 },
    varlColossiBossHiss03: { id: 8180 },
    varlColossiBossShortHiss01: { id: 8184 },
    varlColossiBossShieldslamCharge03: { id: 8189 },
    varlColossiBossMeleeAttackTelegraphFootstep01: { id: 8195 },
    varlColossiBossFootsteps05: { id: 8204 },
    varlColossiBossMeleeAttackTelegraphBass03: { id: 8206 },
    varlColossiBossMeleeAttackTelegraphCharge04: { id: 8207 },
    varlColossiBossMeleeAttackTelegraphFootstep07: { id: 8208 },
    varlColossiBossDeathMetallicImpact01: { id: 8212 },
    varlColossiBossDeathFirstFall02: { id: 8214 },
    varlColossiBossMeleeAttackTelegraphFootstep04: { id: 8215 },
    varlColossiBossFootsteps07: { id: 8216 },
    varlColossiBossHiss03b: { id: 8220 },
    varlColossiBossMeleeAttackTelegraphTridentEndMove07: { id: 8221 },
    varlColossiBossMeleeAttackTelegraphTridentEndMove06: { id: 8222 },
    varlColossiBossMeleeAttackTelegraphStabMetal02: { id: 8224 },
    varlColossiBossMeleeAttackTelegraphTridentEndMove01: { id: 8232 },
    varlColossiBossShieldslamCharge04: { id: 8233 },
    varlColossiBossFootsteps01: { id: 8234 },
    varlColossiBossMeleeAttackTelegraphCharge07: { id: 8238 },
    varlColossiBossMeleeAttackTelegraphTridentEndMove10: { id: 8240 },
    varlColossiBossDeathSecondFall05: { id: 8243 },
    varlColossiBossDeathSecondFall06: { id: 8244 },
    varlColossiBossFootsteps08: { id: 8248 },
    varlColossiBossDeathFirstShieldDrop01: { id: 8255 },
    varlColossiBossShieldslamSlam02: { id: 8260 },
    varlColossiBossDeathRinging01: { id: 8263 },
    varlColossiBossMeleeAttackTelegraphDislodge04: { id: 8264 },
    varlColossiBossMeleeAttackTelegraphCharge12: { id: 8267 },
    varlColossiBossHiss01: { id: 8271 },
    varlColossiBossMeleeAttackTelegraphDislodge03: { id: 8272 },
    varlColossiBossDeathSecondFall04: { id: 8280 },
    varlColossiBossShieldslamSlam01: { id: 8282 },
    varlColossiBossTripleAttackFootstep01: { id: 8283 },
    varlColossiBossDeathLandRumble01: { id: 8284 },
    varlColossiBossMeleeAttackTelegraphCharge10: { id: 8285 },
    varlColossiBossShortHiss04: { id: 8286 },
    varlColossiBossMeleeAttackTelegraphStabMetal01: { id: 8288 },
    varlColossiBossGrappleAttackShieldDrop01: { id: 8290 },
    varlColossiBossMeleeAttackTelegraphLunge04: { id: 8291 },
    varlColossiBossMeleeAttackTelegraphCharge09: { id: 8294 },
    varlColossiBossDeathFirstShieldDrop03: { id: 8300 },
    varlColossiBossMeleeAttackTelegraphTridentEndMove09: { id: 8302 },
    varlColossiBossMeleeAttackTelegraphCharge14: { id: 8303 },
    varlColossiBossHiss04: { id: 8304 },
    varlColossiBossMeleeAttackTelegraphStabImpact06: { id: 8308 },
    varlColossiBossShieldslamArmPrep02: { id: 8310 },
    varlColossiBossMeleeAttackTelegraphDislodge05: { id: 8315 },
    varlColossiBossDeathWhoosh02: { id: 8316 },
    varlColossiBossShieldslamCharge02: { id: 8318 },
    varlColossiBossMeleeAttackTelegraphTridentEndMove11: { id: 8319 },
    varlColossiBossMeleeAttackTelegraphStab04: { id: 8320 },
    varlColossiBossShieldslamArmPrep04: { id: 8322 },
    varlColossiBossDeathFirstFall01: { id: 8323 },
    varlColossiBossDeathFirstShieldDrop04: { id: 8324 },
    varlColossiBossMeleeAttackTelegraphLunge02: { id: 8325 },
    varlColossiBossShieldslamArmPrep01: { id: 8326 },
    varlColossiBossTripleAttackFootstep02: { id: 8327 },
    varlColossiBossMeleeAttackTelegraphTridentEndMove08: { id: 8328 },
    varlColossiBossMeleeAttackTelegraphLunge05: { id: 8331 },
    varlColossiBossFootsteps04: { id: 8332 },
    varlColossiBossMeleeAttackTelegraphTridentEndMove04: { id: 8333 },
    varlColossiBossMeleeAttackTelegraphCharge11: { id: 8335 },
    varlColossiBossMeleeAttackTelegraphFootstep05: { id: 8336 },
    varlColossiBossDeathFirstWhoosh01: { id: 8337 },
  },
} as const satisfies CacheAssets;
